import {
  Clock,
  Users,
  MapPin,
  DollarSign,
  Calendar,
  Sun,
  Award,
  GraduationCap,
  MessageCircle,
  FileText,
  Target,
  CheckCircle2,
  Smartphone,
} from 'lucide-react';
import Year3FoundationsTemplate, {
  type Year3FoundationsPageData,
} from './templates/Year3FoundationsTemplate';

const PAGE: Year3FoundationsPageData = {
  route: '/programs/vic/year-1-foundations-english-tutoring',
  breadcrumb: ['VIC programs', 'Foundations', 'Year 1 English'],
  pill: 'Year 1 · VIC curriculum',
  h1: 'Year 1 English tutoring in VIC',
  subtitle:
    "Build reading, writing and phonics in small groups - aligned to the Victorian Curriculum Level 1 English syllabus.",
  quickFacts: [
    { icon: Clock, text: '{DURATION_SHORT}' },
    { icon: Users, text: '8-12 students' },
    { icon: MapPin, text: 'In-centre + online' },
    { icon: DollarSign, text: '{PRICE} / session' },
  ],
  courseDetailsHeading: 'Year 1 English',
  pricingHeading: 'Year 1 English',
  relatedHeading: 'More for Year 1 students',
  yearLevel: 1,
  stateCode: 'VIC',
  tabs: [
    {
      id: 'term',
      icon: Calendar,
      name: 'Term course',
      sub: '10 weekly sessions per term',
      items: [
        {
          label: 'Overview',
          name: 'Course overview',
          heading: 'Term Course overview',
          para: "The Year 1 English term course runs 10 sessions per term, {DURATION_LENGTH} each, in small groups of 8-12 students. Lessons are aligned to the Victorian Curriculum Level 1 English outcomes and designed to complement what students are learning at school.",
          bullets: ["Reading, writing, phonics and oral language covered across the term","Content sequenced alongside the school year so tutoring and classroom work reinforce each other","Early literacy habits built through consistent, guided practice"],
        },
        {
          label: 'Audience',
          name: "Who it's for",
          heading: "Who it's for",
          para: 'This course is ideal for students who…',
          bullets: ["Want to develop strong early reading and writing habits","Need extra support with phonics, decoding or sentence writing","Are confident readers who want to be challenged further","Benefit from a structured, small-group learning environment","(Parents) want progress to be visible and reported each term"],
        },
        {
          label: 'Benefits',
          name: "What you'll gain",
          heading: "What you'll gain",
          para: 'By the end of the term your child will have made measurable progress in:',
          bullets: ["Reading - phonics, decoding, fluency and comprehension","Writing - sentence construction, simple paragraphs and creative writing","Grammar and punctuation - capital letters, full stops, sight words","Speaking and listening - confidence to share ideas in a group"],
        },
        {
          label: 'Content',
          name: "What's covered",
          heading: "What's covered",
          para: 'Topics are sequenced across the four school terms:',
          bullets: ["Term 1 - Phonics and reading foundations: letter sounds, sight words, fluency","Term 2 - Sentence writing: capital letters, full stops, simple paragraphs","Term 3 - Creative writing: narrative structure, descriptive vocabulary","Term 4 - Comprehension and consolidation: reading for meaning and review"],
        },
        {
          label: 'Program',
          name: 'How it works',
          heading: 'How it works',
          para: 'Every session follows a consistent structure so students always know what to expect:',
          bullets: [
            'Free level assessment before the first session - no wasted lessons on work already mastered',
            'Each 60-min lesson: introduce the concept → model examples → guided practice → independent application',
            "Written and verbal feedback on every student's work each session",
            "End-of-term progress report so parents can see clearly what's improved",
          ],
        },
      ],
    },
    {
      id: 'holiday',
      icon: Sun,
      name: 'Holiday intensive',
      sub: '4 daily lessons in the holidays',
      items: [
        {
          label: 'Overview',
          name: 'Course overview',
          heading: 'Holiday intensive overview',
          para: "The Year 1 English holiday intensive runs 4 daily sessions over the school holidays, 60 minutes each. The program focuses on one literacy skill intensively, allowing students to build confidence quickly.",
          bullets: [
            '4 sessions, daily, 60 minutes each',
            'One skill - deep focus rather than broad coverage',
            'Same small groups and qualified educators as the term course',
            'No prior Schoolola enrolment needed',
          ],
        },
        {
          label: 'Audience',
          name: "Who it's for",
          heading: "Who it's for",
          para: 'The holiday intensive suits students who…',
          bullets: ["Are building confidence in reading or writing","Want a focused short boost before the next school term begins","Can't commit to a full term but want structured, quality tuition","Need to build confidence in one specific literacy skill quickly"],
        },
        {
          label: 'Benefits',
          name: "What you'll gain",
          heading: "What you'll gain",
          para: 'After 4 sessions your child will have:',
          bullets: ["A solid understanding of one literacy skill - structure, language, practice approach","Strategies for tackling reading and writing tasks at school","Increased confidence to participate in class literacy activities","Specific written feedback on their own practice work"],
        },
        {
          label: 'Content',
          name: "What's covered",
          heading: "What's covered",
          para: 'The four sessions follow a structured daily sequence:',
          bullets: ["Day 1 - Skill overview: model texts, key features, simple framework","Day 2 - Guided practice: scaffolded reading or writing tasks","Day 3 - Independent application with educator support","Day 4 - Showcase activity with full written feedback"],
        },
        {
          label: 'Program',
          name: 'How it works',
          heading: 'How it works',
          para: 'The intensive is designed for maximum impact across four consecutive sessions:',
          bullets: [
            'Register your interest - sessions fill quickly in school holiday periods',
            'Each day builds on the previous - daily attendance is expected',
            'Individual written feedback on Day 3 and Day 4 tasks included',
            'Students leave with a completed practice portfolio',
          ],
        },
      ],
    },
  ],
  inclusions: [
    {
      icon: Award,
      name: 'Qualified educators',
      desc: 'Teachers with VIC curriculum expertise and classroom experience - consistent across all VIC centres.',
    },
    {
      icon: Users,
      name: 'Small group classes',
      desc: '8-12 students per class. Your child gets seen, heard and responded to every lesson.',
    },
    {
      icon: GraduationCap,
      name: 'VIC curriculum-aligned',
      desc: "Content mapped directly to Victorian Curriculum Level 1 outcomes. Complements school, session by session.",
    },
    {
      icon: MessageCircle,
      name: 'Feedback every lesson',
      desc: "Structured written and verbal feedback on every student's work, every session.",
    },
    {
      icon: FileText,
      name: 'End-of-term report',
      desc: 'A clear parent-facing progress summary at end of each term - before school reports arrive.',
    },
    {
      icon: Target,
      name: 'Free assessment',
      desc: 'Level placement before the first session. No wasted lessons on work already mastered.',
    },
    {
      icon: CheckCircle2,
      name: 'Strong foundations',
      desc: "Early literacy habits built into the term sequence - a strong base for NAPLAN in later years.",
    },
    {
      icon: Smartphone,
      name: 'Schoolola LMS',
      desc: 'Access theory lesson videos, online practice questions and track progress easily via the Schoolola LMS app.',
      images: [
        '/lovable-uploads/lms-parent-dashboard.png',
        '/lovable-uploads/lms-learning-dashboard.png',
        '/lovable-uploads/lms-diagnostic-overview.png',
        '/lovable-uploads/lms-quiz-question-1.png',
        '/lovable-uploads/lms-quiz-question-2.png',
        '/lovable-uploads/lms-results-report.png',
      ],
    },
  ],
  faq: [
    {
      q: 'Is {PRICE} the full price - are there any extras?',
      a: '{PRICE} covers the session. Term enrolments include the end-of-term progress report at no extra cost. {BOOKS}',
    },
    {
      q: 'What study materials are provided?',
      a: 'Study materials are provided as bound books - no loose papers. {BOOKS}',
    },
    {
      q: 'How is this different from what happens at school?',
      a: "Group sizes are smaller (8-12 students per class), lessons follow a consistent structured format, and every student receives written feedback on their own work each session. Content follows the same Victorian Curriculum Level 1 outcomes - we're giving your child more practice and more attention within the same curriculum.",
    },
    {
      q: 'Is this the right level for my Year 1 child?',
      a: 'The free assessment will confirm placement. If your child is in Year 1 and you want to strengthen early literacy or numeracy, this is the right starting point. The assessment happens before the first session.',
    },
    {
      q: 'What if my child misses a session?',
      a: 'We ask for consistent attendance as each session builds on the last. If a session is missed, your educator can note the key material to follow up at home - but there are no standard make-up classes. Contact your local centre.',
    },
    {
      q: 'Can a confident Year 1 student still be challenged?',
      a: 'Yes. The assessment places students within ability groups, not just year groups. A Year 1 student working above level will be placed in a group that challenges them accordingly.',
    },
  ],
  related: [
    {
      tag: 'Year 1 · VIC',
      name: 'Year 1 Maths',
      desc: "Number sense, counting and early problem solving",
      href: '/programs/vic/year-1-foundations-maths-tutoring',
    },
  ],
  seo: {
    title: 'Year 1 English Tutoring VIC - Small Groups | Schoolola',
    description:
      'Year 1 English tutoring in VIC. Victorian Curriculum Level 1 aligned phonics, reading, writing and early literacy. Small groups of 8-12 students..',
    schemaCourseName: 'Year 1 English Tutoring - VIC',
    schemaDescription:
      'Year 1 English tuition in VIC. Small groups of 8-12 students, aligned to Victorian Curriculum Level 1. phonics, reading, writing and early literacy..',
  },
};

export default function VicYear1FoundationsEnglish() {
  return <Year3FoundationsTemplate page={PAGE} />;
}
